import { type ReactElement } from "react"
import { Link } from "react-router-dom"
import { HeroHeader } from "@/components/ui/hero-section-1"
import { AnimatedGroup } from "@/components/ui/animated-group"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { TextEffect } from "@/components/ui/text-effect"
import { cn } from "@/lib/utils"
import {
  Building2,
  LucideIcon,
  MapPin,
  Quote,
  Sparkles,
  Trophy,
  Users,
  ArrowRight,
  TrendingUp,
  Clock,
  Zap,
  Star
} from "lucide-react"

type Story = {
  name: string
  title: string
  metric: string
  quote: string
  icon: LucideIcon
  location: string
  highlight: string
}

const operatorStories: Story[] = [
  {
    name: "The Night Bazaar",
    title: "Food hall + curbside lockers",
    metric: "Throughput +32%",
    quote: "Braise orchestrates 14 vendors as if we’re one restaurant. Automations reroute runners before we even notice a locker slowing down.",
    icon: Trophy,
    location: "Austin, TX",
    highlight: "14 vendors, 6 automations live",
  },
  {
    name: "Fleet Kitchen",
    title: "Festival + stadium operations",
    metric: "Guest sentiment +18 NPS",
    quote: "Our pop-ups pivot on the fly. When a bar backs up, Braise pauses promos and spins up another prep line without waiting for a radio call.",
    icon: Sparkles,
    location: "Chicago, IL",
    highlight: "Served 45k guests in one weekend",
  },
  {
    name: "SideDoor Trucks",
    title: "Mobile fleet POS orchestration",
    metric: "Prep variance -24%",
    quote: "Before Braise, handhelds were chaos. Now every truck sees the same signal, and automations trigger restock runs at the perfect moment.",
    icon: Users,
    location: "Los Angeles, CA",
    highlight: "18 mobile units synced",
  },
]

const momentumSignals = [
  {
    label: "Orders orchestrated",
    value: "92M+",
    detail: "Across QR, kiosk, handheld, counter, and delivery rails.",
    icon: TrendingUp,
    color: "bg-blue-50 text-blue-700 border-blue-100"
  },
  {
    label: "Go-live timeline",
    value: "10 days",
    detail: "From kickoff to first live venue with coaching.",
    icon: Clock,
    color: "bg-amber-50 text-amber-700 border-amber-100"
  },
  {
    label: "Automation adoption",
    value: "78%",
    detail: "Venues running at least five automations within 30 days.",
    icon: Zap,
    color: "bg-purple-50 text-purple-700 border-purple-100"
  },
  {
    label: "Crew satisfaction",
    value: "4.7 / 5",
    detail: "Average rating from post-shift surveys run via Braise.",
    icon: Star,
    color: "bg-emerald-50 text-emerald-700 border-emerald-100"
  },
]

const spotlightStories = [
  {
    title: "Orchestrating a stadium double-header",
    summary: "Braise helped Market Rail operate two overlapping games by forecasting demand, pre-positioning inventory, and automating staff alerts.",
    result: "Merch + F&B revenue +21% night-of",
    link: "#",
  },
  {
    title: "From pop-up chaos to repeatable playbook",
    summary: "Night Bazaar’s pop-ups now run on a reusable automation kit-kiosk throttling, VIP alerts, and sensor-driven locker management.",
    result: "Line abandonments -35% across markets",
    link: "#",
  },
  {
    title: "Drive-thru and curbside, orchestrated",
    summary: "ChefLine connected kitchen displays, locker telemetry, and guest messaging so late-night drive-thru runs never stall out.",
    result: "Drive-thru dwell down to 2m 10s average",
    link: "#",
  },
]

const StoriesPageContent = (): ReactElement => (
  <div className="min-h-screen bg-white text-slate-900 selection:bg-emerald-100 selection:text-emerald-900 font-sans">
    <HeroHeader />
    <main className="relative">
      {/* Hero Section */}
      <section className="relative isolate overflow-hidden pb-32 pt-36 md:pb-44 md:pt-44">
        <div className="absolute inset-0 -z-20 bg-[radial-gradient(135%_130%_at_50%_-15%,rgba(16,185,129,0.1),transparent_55%)]" />
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(120%_130%_at_50%_110%,rgba(16,185,129,0.05),rgba(255,255,255,0.95))]" />

        <AnimatedGroup
          variants={{
            container: {
              visible: { transition: { staggerChildren: 0.1, delayChildren: 0.12 } },
            },
            item: {
              hidden: { opacity: 0, filter: "blur(14px)", y: 24 },
              visible: {
                opacity: 1,
                filter: "blur(0px)",
                y: 0,
                transition: { type: "spring", duration: 1.1, bounce: 0.26 },
              },
            },
          }}
          className="mx-auto flex max-w-5xl flex-col items-center gap-8 px-6 text-center"
        >
          <Badge className="w-fit rounded-full border border-emerald-200 bg-emerald-50 px-5 py-2 text-[0.65rem] uppercase tracking-[0.32em] text-emerald-700 shadow-sm">
            Customer Stories
          </Badge>
          <h1 className="text-balance text-5xl font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-6xl lg:text-7xl">
            See how leaders <span className="text-emerald-600">orchestrate</span> <br className="hidden md:block" /> every signal.
          </h1>
          <p className="max-w-2xl text-lg leading-relaxed text-slate-600 md:text-xl">
            From stadium concourses to neighborhood curbside, operators rely on Braise to keep crews synchronized and guests delighted.
          </p>
        </AnimatedGroup>
      </section>

      {/* Momentum Signals - Bento Grid */}
      <section className="relative -mt-20 px-6 pb-24 md:pb-32">
        <div className="mx-auto max-w-6xl">
          <AnimatedGroup
            variants={{
              container: {
                visible: { transition: { staggerChildren: 0.1 } },
              },
              item: {
                hidden: { opacity: 0, y: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { type: "spring", duration: 0.8 },
                },
              },
            }}
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            {momentumSignals.map(({ label, value, detail, icon: Icon, color }) => (
              <div key={label} className="group relative overflow-hidden rounded-[2rem] border border-slate-100 bg-white p-8 shadow-[0_20px_50px_rgba(15,23,42,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_25px_60px_rgba(15,23,42,0.08)]">
                <div className={cn("mb-6 flex size-12 items-center justify-center rounded-2xl border", color)}>
                  <Icon className="size-6" />
                </div>
                <div className="space-y-2">
                  <TextEffect preset="blur" per="char" as="p" className="text-4xl font-bold tracking-tight text-slate-900">
                    {value}
                  </TextEffect>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">{detail}</p>
              </div>
            ))}
          </AnimatedGroup>
        </div>
      </section>

      {/* Operator Stories - Staggered Layout */}
      <section className="relative py-24 md:py-32 bg-slate-50/50">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.05),transparent_40%)]" />
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-16 md:mb-24">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Voices from the front lines</h2>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {operatorStories.map(({ name, title, metric, quote, icon: Icon, location, highlight }, i) => (
              <article
                key={name}
                className={cn(
                  "relative flex flex-col justify-between overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white p-8 shadow-sm transition-all duration-500 hover:shadow-xl",
                  i === 1 && "md:-mt-12 md:mb-12" // Stagger effect
                )}
              >
                <div className="mb-8 flex items-start justify-between">
                  <div className="flex size-14 items-center justify-center rounded-2xl bg-slate-50 text-slate-900 border border-slate-100">
                    <Icon className="size-7" strokeWidth={1.5} />
                  </div>
                  <Badge variant="outline" className="border-slate-200 bg-white text-slate-500 font-medium">
                    {location}
                  </Badge>
                </div>

                <div className="space-y-6">
                  <blockquote className="relative">
                    <Quote className="absolute -left-2 -top-2 size-8 text-emerald-100/50 -scale-x-100 fill-emerald-50" />
                    <p className="relative text-xl font-medium leading-relaxed text-slate-900">
                      {quote}
                    </p>
                  </blockquote>

                  <div className="border-t border-slate-100 pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-slate-900">{name}</h3>
                        <p className="text-sm text-slate-500">{title}</p>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-transparent">
                        {metric}
                      </Badge>
                      <Badge variant="outline" className="text-slate-500 border-slate-200">
                        {highlight}
                      </Badge>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Spotlights Section */}
      <section className="relative py-24 md:py-32">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mx-auto max-w-3xl text-center mb-16">
            <Badge className="mb-6 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1 text-[0.65rem] uppercase tracking-[0.32em] text-emerald-700">
              Field Notes
            </Badge>
            <h2 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">Deep dives & playbooks</h2>
            <p className="mt-4 text-xl text-slate-600">
              Detailed breakdowns of how top operators plan, launch, and optimize.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {/* Featured Spotlight (First item spans 2 cols on large screens) */}
            {spotlightStories.map(({ title, summary, result, link }, index) => (
              <div
                key={title}
                className={cn(
                  "group relative flex flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm transition-all hover:border-emerald-200 hover:shadow-md",
                  index === 0 ? "lg:col-span-2 bg-slate-900 text-white border-slate-800" : ""
                )}
              >
                {index === 0 && (
                  <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.2),transparent_50%)]" />
                )}

                <div className="mb-auto">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className={cn("size-5", index === 0 ? "text-emerald-400" : "text-emerald-600")} />
                    <span className={cn("text-xs font-bold uppercase tracking-widest", index === 0 ? "text-slate-400" : "text-slate-500")}>Case Study</span>
                  </div>
                  <h3 className={cn("text-2xl font-bold mb-3", index === 0 ? "text-white sm:text-3xl" : "text-slate-900")}>{title}</h3>
                  <p className={cn("text-base leading-relaxed", index === 0 ? "text-slate-300 max-w-lg" : "text-slate-600")}>{summary}</p>
                </div>

                <div className="mt-8 pt-8 border-t border-dashed border-slate-200/20">
                  <div className="flex items-center justify-between">
                    <span className={cn("text-sm font-medium", index === 0 ? "text-emerald-400" : "text-emerald-700")}>{result}</span>
                    <Button asChild variant="ghost" className={cn("group/btn p-0 hover:bg-transparent", index === 0 ? "text-white hover:text-emerald-400" : "text-slate-900 hover:text-emerald-600")}>
                      <Link to={link} className="flex items-center gap-2">
                        Read <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="mx-auto max-w-5xl px-6">
          <div className="relative overflow-hidden rounded-[3rem] bg-emerald-900 px-6 py-20 text-center shadow-2xl sm:px-16">
            {/* Background Effects */}
            <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,rgba(52,211,153,0.2),transparent_50%)]" />
            <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_bottom_right,rgba(6,78,59,0.8),transparent_50%)]" />

            <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Ready to orchestrate your operations?
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg text-emerald-100">
              Join the operators running 92M+ orders annually on Braise. Guided onboarding included.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button asChild size="lg" className="h-14 rounded-full bg-white px-8 text-base font-semibold text-emerald-950 hover:bg-emerald-50">
                <Link to="/pricing">View Plans</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-14 rounded-full border-emerald-700 bg-transparent px-8 text-base font-semibold text-white hover:bg-emerald-800 hover:text-white hover:border-emerald-600">
                <Link to={{ pathname: "/", hash: "demo" }}>Interactive Demo</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
)

const StoriesPage = (): ReactElement => <StoriesPageContent />

export default StoriesPage
